import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Soup, Fish, Flame, Leaf, Cookie, IceCream, Menu, ChevronLeft, ChevronRight, Clock, X, Maximize2 } from "lucide-react";
import { IMG, CONTACT } from "@/veedu/data";
import { useOpeningHours } from "@/veedu/openingHours";
import { useReservation } from "@/context/ReservationContext";

/* ── Botanical Corner SVGs (Matching Reference Image) ──────────── */

/** Top-Right Cascading Leaf Branch */
const TopRightBotanical = () => (
  <svg
    viewBox="0 0 320 320"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute top-0 right-0 w-72 md:w-96 h-auto pointer-events-none select-none z-0 opacity-75"
  >
    {/* Soft watercolor background stain */}
    <ellipse cx="250" cy="70" rx="100" ry="80" fill="#E5D8CA" opacity="0.6" filter="blur(22px)" />
    <ellipse cx="220" cy="130" rx="70" ry="90" fill="#D3C1AF" opacity="0.4" filter="blur(28px)" />

    {/* Main stem */}
    <path
      d="M310 10 Q240 60 190 160 Q160 225 140 300"
      stroke="#2F5D50"
      strokeWidth="2.2"
      strokeLinecap="round"
      opacity="0.8"
    />
    <path
      d="M310 10 Q240 60 190 160 Q160 225 140 300"
      stroke="#1E3E35"
      strokeWidth="1.1"
      strokeLinecap="round"
      opacity="0.5"
    />

    {/* Cascading leaves */}
    {[
      { path: "M280 35 Q220 25 200 45 Q240 65 280 35 Z", fill: "#3B6E60" },
      { path: "M250 65 Q290 80 300 110 Q260 105 250 65 Z", fill: "#2F5D50" },
      { path: "M230 85 Q180 80 170 110 Q210 120 230 85 Z", fill: "#477D6E" },
      { path: "M210 120 Q250 140 255 175 Q220 165 210 120 Z", fill: "#2A5247" },
      { path: "M195 150 Q145 155 140 185 Q180 190 195 150 Z", fill: "#3B6E60" },
      { path: "M180 185 Q220 210 225 245 Q190 225 180 185 Z", fill: "#477D6E" },
      { path: "M160 220 Q120 230 115 260 Q150 260 160 220 Z", fill: "#2F5D50" },
      { path: "M150 255 Q180 280 180 310 Q155 295 150 255 Z", fill: "#3B6E60" }
    ].map((leaf, idx) => (
      <g key={idx}>
        <path d={leaf.path} fill={leaf.fill} opacity="0.85" />
        <path
          d={leaf.path.split(" ")[0] + " " + leaf.path.split(" ")[1] + " " + leaf.path.split(" ")[2]}
          stroke="#F6F2EA"
          strokeWidth="0.6"
          opacity="0.5"
        />
      </g>
    ))}
  </svg>
);

/** Bottom-Left Leaf & Spice Accent */
const BottomLeftBotanical = () => (
  <svg
    viewBox="0 0 300 300"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute bottom-0 left-0 w-64 md:w-80 h-auto pointer-events-none select-none z-0 opacity-75"
  >
    {/* Terracotta/clay watercolor splash */}
    <circle cx="60" cy="240" r="90" fill="#E5C7B7" opacity="0.65" filter="blur(28px)" />
    <circle cx="95" cy="200" r="60" fill="#D49F86" opacity="0.45" filter="blur(22px)" />

    {/* Leaf sprig rising */}
    <path d="M10 290 Q70 230 140 170 Q185 125 230 90" stroke="#3B6E60" strokeWidth="2" strokeLinecap="round" opacity="0.75" />
    <path d="M45 255 Q10 220 20 195 Q55 212 45 255 Z" fill="#2F5D50" opacity="0.85" />
    <path d="M80 225 Q115 200 125 170 Q90 182 80 225 Z" fill="#477D6E" opacity="0.85" />
    <path d="M110 195 Q80 160 85 138 Q120 155 110 195 Z" fill="#3B6E60" opacity="0.85" />

    {/* Dried chili pod */}
    <path
      d="M35 275 C12 250 18 215 52 205 C58 211 35 240 35 275 Z"
      fill="#A8321D"
      opacity="0.9"
    />
    <path d="M52 205 Q64 193 60 188" stroke="#477D6E" strokeWidth="1.6" strokeLinecap="round" opacity="0.9" />

    {/* Spice powder dust dots */}
    {[
      { cx: 22, cy: 260, r: 2.8, color: "#C4572D" },
      { cx: 38, cy: 280, r: 1.8, color: "#C2A04A" },
      { cx: 58, cy: 270, r: 2.2, color: "#A8321D" },
      { cx: 75, cy: 250, r: 1.4, color: "#C2A04A" },
      { cx: 18, cy: 245, r: 2.0, color: "#A8321D" },
      { cx: 90, cy: 275, r: 2.5, color: "#C4572D" }
    ].map((dot, i) => (
      <circle key={i} cx={dot.cx} cy={dot.cy} r={dot.r} fill={dot.color} opacity="0.65" />
    ))}
  </svg>
);

/* ── Category Icon Map & Fallback Definitions ─────────────────── */
const CATEGORY_ICON_MAP = {
  "house-snacks": Sparkles,
  "tasting-menu": Sparkles,
  "small-plates": Soup,
  "from-tandoor": Flame,
  "coastal": Fish,
  "signature-curries": Flame,
  "rice-pilau": Soup,
  "biriyani": Soup,
  "perfect-pairings": Sparkles,
  "breads": Cookie,
  "vegetarian": Leaf,
  "classic-curries": Flame,
  "signature-sharing": Sparkles,
  "desserts": IceCream,
  "full-menu": Menu,
};


/* ── Dietary Indicators (Reference Match) ──────────────────── */

/** Green leaf icon for Veg */
const VegLeafIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 text-[#2F5D50]">
    <path
      d="M13.5 2.5C7.5 2.5 3.5 6.5 3.5 12.5C5.5 12.5 9.5 11.5 11.5 8.5C13.5 5.5 13.5 2.5 13.5 2.5Z"
      stroke="#2F5D50"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path d="M3.5 12.5Q7.5 8.5 11.5 8.5" stroke="#2F5D50" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

/** Red square dot badge for Non-Veg */
const NonVegBadgeIcon = () => (
  <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-[#A8321D]/80 p-0.5">
    <span className="h-1.5 w-1.5 rounded-full bg-[#A8321D]" />
  </span>
);

/** Red Chilli icon for Spice level */
const ChilliIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg viewBox="0 0 20 20" fill="none" className={`${className} inline-block shrink-0`} xmlns="http://www.w3.org/2000/svg">
    {/* Green Curved Stem */}
    <path
      d="M10.5 4.5 C11.5 2.2 13.5 1.5 15.2 1.2"
      stroke="#227144"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    {/* Red Chilli Body */}
    <path
      d="M6.5 4.8 C4.2 7.2 3.6 11 5 13.8 C6.6 16.6 9.4 18.2 11.8 18.5 C11 16.8 9.6 14.8 8.8 12.2 C8 9.2 9.4 6.8 13 4.8 Z"
      fill="#DC2626"
    />
    {/* Shiny highlight on outer curve */}
    <path
      d="M5.2 8.5 C4.8 11 5.4 13.4 6.8 15.4"
      stroke="#FCA5A5"
      strokeWidth="0.8"
      strokeLinecap="round"
      opacity="0.7"
    />
    {/* Green Calyx / 3-Point Crown Cap */}
    <path
      d="M6 4.6 C7.5 3.8 12 3.8 13.5 4.6 C13.2 6 12.2 6.6 11.5 5.8 C10.8 6.6 9.2 6.6 8.5 5.8 C7.8 6.6 6.8 6 6 4.6 Z"
      fill="#227144"
    />
  </svg>
);

const SpiceLevelIcons = ({ level }) =>
  level > 0 ? (
    <div className="flex items-center gap-0.5 select-none" title={`Spice level: ${level}/3`}>
      {Array.from({ length: level }).map((_, i) => (
        <ChilliIcon key={i} />
      ))}
    </div>
  ) : null;

/** Contains Gluten badge */
const ContainsGlutenIcon = () => (
  <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-[#8C7462]/60 bg-[#EBE3D5] text-[7.5px] font-bold text-[#604B3E] tracking-tighter" title="Contains Gluten">
    G
  </span>
);

/** Contains Nut badge */
const ContainsNutIcon = () => (
  <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-[#4A2F25]/60 bg-[#EBE3D5] text-[7.5px] font-bold text-[#4A2F25] tracking-tighter" title="Contains Nut">
    N
  </span>
);

/** Contains Dairy/Lactose badge */
const ContainsDairyIcon = () => (
  <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-[#C4572D]/60 bg-[#EBE3D5] text-[7.5px] font-bold text-[#C4572D] tracking-tighter" title="Contains lactose/Dairy">
    D
  </span>
);

/* ── Image Resolver with multi-origin fallback ─────────────── */
const resolveImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const baseUrl = process.env.REACT_APP_BACKEND_URL || (isLocal ? "https://localhost:7134" : "https://api.chefjomonsveedu.co.uk");

  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
};

/* ── Menu Item Row (Interactive Enlargeable View) ───────────── */
const MenuItemRow = ({ item, onSelect }) => {
  const inactive = item.active === false;
  const initialImage = resolveImageUrl(item.image);
  const [imageSrc, setImageSrc] = useState(initialImage);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setImageSrc(resolveImageUrl(item.image));
    setHasImageError(false);
  }, [item.image]);

  const handleImageError = () => {
    // If https://localhost:7134 failed (e.g. self-signed SSL cert in browser), try fallback to http://localhost:5011
    if (imageSrc && imageSrc.includes("localhost:7134")) {
      setImageSrc(imageSrc.replace("https://localhost:7134", "http://localhost:5011"));
    } else {
      setHasImageError(true);
    }
  };

  const handleSelect = () => {
    if (onSelect) onSelect(item);
  };

  return (
    <div
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleSelect()}
      className={`group relative flex items-center gap-4 md:gap-5 py-3.5 px-3 rounded-2xl transition-all duration-300 cursor-pointer hover:bg-[#EBE2D3]/70 hover:shadow-sm focus:outline-none focus:ring-1 focus:ring-[#C2A04A]/50 ${inactive ? "opacity-40" : ""}`}
      title="Click to view & enlarge photo"
    >
      {/* 1. Ceramic Plate Image (Enlargeable on Click) */}
      <div className="relative shrink-0 w-20 h-20 md:w-24 md:h-24">
        <div className="absolute inset-1 rounded-full bg-[#382218]/12 blur-md transform translate-y-1 group-hover:scale-105 transition-transform duration-300" />
        <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-[#D8C9B8] shadow-md bg-[#EBE3D5] group-hover:border-[#C2A04A] transition-all duration-300">
          {imageSrc && !hasImageError ? (
            <img
              src={imageSrc}
              alt={item.name}
              loading="lazy"
              onError={handleImageError}
              className="w-full h-full object-cover scale-[1.08] transition-transform duration-500 group-hover:scale-115"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#E5DCCB]">
              <span className="font-display text-xl text-[#8C7462]">✦</span>
            </div>
          )}

          {/* Hover Enlarge Hint Overlay */}
          <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-full">
            <Maximize2 size={16} className="text-[#F6F2EA] drop-shadow-md transform scale-90 group-hover:scale-100 transition-transform" />
          </div>
        </div>
      </div>

      {/* 2. Middle Content (Name & Description) */}
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h4 className="font-display text-base md:text-lg lg:text-xl font-normal tracking-tight text-[#382218] group-hover:text-[#A8321D] transition-colors leading-tight">
            {item.name}
          </h4>
          {item.featured && (
            <span className="font-body text-[7.5px] tracking-[0.25em] uppercase text-[#A8321D] font-semibold bg-[#A8321D]/10 px-2 py-0.5 rounded-sm">
              Chef's Pick
            </span>
          )}
        </div>
        <p className="mt-1 font-body text-xs md:text-[13px] font-light leading-relaxed text-[#604B3E]">
          {item.desc}
        </p>
        {inactive && (
          <span className="mt-1 inline-block font-body text-[8.5px] tracking-[0.2em] uppercase text-[#A8321D]">
            Unavailable Today
          </span>
        )}
      </div>

      {/* 3. Right Content (Dietary Icons) */}
      <div className="shrink-0 flex flex-col items-end justify-center min-w-[50px]">
        <div className="flex flex-wrap justify-end items-center gap-1.5">
          {item.veg && <VegLeafIcon />}
          {item.spiceLevel > 0 && <SpiceLevelIcons level={item.spiceLevel} />}
          {(item.cg || item.gf) && <ContainsGlutenIcon />}
          {(item.cn || item.nut) && <ContainsNutIcon />}
          {(item.cd || item.da) && <ContainsDairyIcon />}
        </div>
      </div>
    </div>
  );
};

/* ── Horizontal Ornamental Line between Dish Rows ───────────── */
const RowOrnamentDivider = () => (
  <div className="flex items-center justify-center my-1 opacity-40">
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#8C7462]/30 to-transparent" />
    <svg viewBox="0 0 24 10" fill="none" className="w-5 h-2 mx-2 text-[#8C7462]">
      <path d="M0 5H8M16 5H24M12 1L14 5L12 9L10 5L12 1Z" stroke="currentColor" strokeWidth="0.8" />
    </svg>
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#8C7462]/30 to-transparent" />
  </div>
);

/* ── Two-Column Menu Canvas Grid ────────────────────────────── */
const MenuCanvasGrid = ({ items = [], onSelect }) => {
  const mid = Math.ceil(items.length / 2);
  const colLeft = items.slice(0, mid);
  const colRight = items.slice(mid);

  return (
    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-x-8 lg:gap-x-16 items-start">
      {/* Center Vertical Divider (Desktop) */}
      <div className="hidden lg:block absolute left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-[#4A2F25]/20 to-transparent -translate-x-1/2 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#8C7462] text-[8px]">✦</div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 text-[#8C7462] text-[8px]">✦</div>
      </div>

      {/* Left Column */}
      <div className="flex flex-col">
        {colLeft.map((item, idx) => (
          <div key={item.id || idx}>
            <MenuItemRow item={item} onSelect={onSelect} />
            {idx < colLeft.length - 1 && <RowOrnamentDivider />}
          </div>
        ))}
      </div>

      {/* Right Column */}
      <div className="flex flex-col mt-4 lg:mt-0">
        {colRight.map((item, idx) => (
          <div key={item.id || idx}>
            <MenuItemRow item={item} onSelect={onSelect} />
            {idx < colRight.length - 1 && <RowOrnamentDivider />}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Tasting Menu 2-Section Categorized View ─────────────────── */
const TastingMenuSectionedView = ({ items = [], onSelect }) => {
  const coastalItems = useMemo(
    () => items.filter((d) => (d.category === "tasting-menu" || d.category === "tasting") && d.journey === "coastal"),
    [items]
  );
  const panIndianItems = useMemo(
    () => items.filter((d) => (d.category === "tasting-menu" || d.category === "tasting") && d.journey === "pan-indian"),
    [items]
  );

  return (
    <div className="w-full space-y-14 md:space-y-16">
      {/* ── Section 1: The Coastal Journey ── */}
      <div>
        <div className="text-center mb-8">
          <span className="font-body text-[9px] tracking-[0.4em] uppercase text-[#A8321D] font-semibold">
            7-COURSE TASTING MENU • PART 01
          </span>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light text-[#382218] tracking-tight mt-1">
            The Coastal Journey
          </h3>
          <div className="flex items-center justify-center gap-3 mt-2">
            <span className="h-px w-10 bg-[#4A2F25]/20" />
            <span className="text-[#A8321D] text-xs">✤</span>
            <span className="h-px w-10 bg-[#4A2F25]/20" />
          </div>
          <p className="mt-2 font-body text-xs md:text-sm font-light italic text-[#604B3E] tracking-wide">
            Wine pairing optional
          </p>
        </div>

        <MenuCanvasGrid items={coastalItems} onSelect={onSelect} />
      </div>

      {/* ── Ornamental Transition Divider between the 2 Journeys ── */}
      <div className="flex items-center justify-center gap-4 my-6 opacity-60">
        <span className="h-px w-16 md:w-28 bg-gradient-to-r from-transparent via-[#4A2F25]/30 to-transparent" />
        <span className="text-[#C2A04A] text-xs">✦ ✦ ✦</span>
        <span className="h-px w-16 md:w-28 bg-gradient-to-r from-transparent via-[#4A2F25]/30 to-transparent" />
      </div>

      {/* ── Section 2: The Pan Indian Journey ── */}
      <div>
        <div className="text-center mb-8">
          <span className="font-body text-[9px] tracking-[0.4em] uppercase text-[#A8321D] font-semibold">
            7-COURSE TASTING MENU • PART 02
          </span>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light text-[#382218] tracking-tight mt-1">
            The Pan Indian Journey
          </h3>
          <div className="flex items-center justify-center gap-3 mt-2">
            <span className="h-px w-10 bg-[#4A2F25]/20" />
            <span className="text-[#A8321D] text-xs">✤</span>
            <span className="h-px w-10 bg-[#4A2F25]/20" />
          </div>
          <p className="mt-2 font-body text-xs md:text-sm font-light italic text-[#604B3E] tracking-wide">
            A celebration of iconic regional flavours across India
          </p>
        </div>

        <MenuCanvasGrid items={panIndianItems} onSelect={onSelect} />
      </div>
    </div>
  );
};

/* ── Full Menu Grouped Block ────────────────────────────────── */
const FullMenuCategoryBlock = ({ cat, allItems = [], onSelect }) => {
  const items = allItems.filter((d) => d.category === cat.id);
  if (!items.length) return null;

  if (cat.id === "tasting-menu") {
    return (
      <div className="mb-14 last:mb-0">
        <TastingMenuSectionedView items={allItems} onSelect={onSelect} />
      </div>
    );
  }

  return (
    <div className="mb-14 last:mb-0">
      <div className="text-center mb-6">
        <span className="font-body text-[8.5px] tracking-[0.4em] uppercase text-[#A8321D] font-semibold">
          {cat.sublabel}
        </span>
        <h3 className="font-display text-2xl md:text-3xl font-light text-[#382218] tracking-tight mt-1">
          {cat.label}
        </h3>
        <div className="flex items-center justify-center gap-3 mt-2">
          <span className="h-px w-10 bg-[#4A2F25]/20" />
          <span className="text-[#A8321D] text-xs">✤</span>
          <span className="h-px w-10 bg-[#4A2F25]/20" />
        </div>
      </div>

      <MenuCanvasGrid items={items} onSelect={onSelect} />
    </div>
  );
};

/* ── Dish Enlarge Lightbox Modal ─────────────────────────────── */
const DishEnlargeModal = ({ item, items = [], categories = [], onClose, onSelect }) => {
  const { openReservationModal } = useReservation();
  const initialImage = resolveImageUrl(item.image);
  const [imageSrc, setImageSrc] = useState(initialImage);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setImageSrc(resolveImageUrl(item.image));
    setHasImageError(false);
  }, [item.image]);

  const handleImageError = () => {
    if (imageSrc && imageSrc.includes("localhost:7134")) {
      setImageSrc(imageSrc.replace("https://localhost:7134", "http://localhost:5011"));
    } else {
      setHasImageError(true);
    }
  };

  // Prev / Next navigation
  const currentIndex = items.findIndex((d) => (d.id && d.id === item.id) || d.name === item.name);
  const totalItems = items.length;

  const handlePrev = useCallback((e) => {
    e?.stopPropagation();
    if (currentIndex > -1 && totalItems > 1) {
      const prevIndex = (currentIndex - 1 + totalItems) % totalItems;
      onSelect(items[prevIndex]);
    }
  }, [currentIndex, items, onSelect, totalItems]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation();
    if (currentIndex > -1 && totalItems > 1) {
      const nextIndex = (currentIndex + 1) % totalItems;
      onSelect(items[nextIndex]);
    }
  }, [currentIndex, items, onSelect, totalItems]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNext, handlePrev, onClose]);

  const catObj = categories.find((c) => c.id === item.category);
  const catLabel = catObj?.label || catObj?.short || item.category;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-5 md:p-8 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl my-auto bg-gradient-to-b from-[#251711] via-[#1F120C] to-[#170E09] text-[#F6F2EA] rounded-3xl border border-[#C2A04A]/30 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button Top Right */}
        <button
          onClick={onClose}
          aria-label="Close enlarged view"
          className="absolute top-4 right-4 z-30 h-10 w-10 rounded-full bg-black/60 hover:bg-[#A8321D] text-white/90 hover:text-white border border-white/20 backdrop-blur-md flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-lg"
        >
          <X size={20} />
        </button>

        {/* Previous Button */}
        {totalItems > 1 && (
          <button
            onClick={handlePrev}
            aria-label="Previous dish"
            className="absolute left-3 top-36 sm:top-44 md:top-48 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-black/60 hover:bg-[#C4572D] text-white/90 hover:text-white border border-white/20 backdrop-blur-md flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg"
          >
            <ChevronLeft size={22} />
          </button>
        )}

        {/* Next Button */}
        {totalItems > 1 && (
          <button
            onClick={handleNext}
            aria-label="Next dish"
            className="absolute right-3 top-36 sm:top-44 md:top-48 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-black/60 hover:bg-[#C4572D] text-white/90 hover:text-white border border-white/20 backdrop-blur-md flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg"
          >
            <ChevronRight size={22} />
          </button>
        )}

        {/* 1. High-Resolution Dish Photo Hero */}
        <div className="relative w-full h-72 sm:h-88 md:h-96 bg-[#130B07] overflow-hidden flex items-center justify-center">
          {imageSrc && !hasImageError ? (
            <motion.img
              key={imageSrc}
              initial={{ scale: 1.05, opacity: 0.85 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.35 }}
              src={imageSrc}
              alt={item.name}
              onError={handleImageError}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="w-24 h-24 rounded-full border-2 border-[#C2A04A]/30 bg-[#2B1B14] flex items-center justify-center shadow-inner">
                <span className="font-display text-4xl text-[#C2A04A]">✦</span>
              </div>
              <span className="mt-3 font-display text-lg text-[#F6F2EA]/80">Freshly Plated Creation</span>
              <span className="text-xs font-body text-[#D3C1AF]/60 uppercase tracking-widest mt-0.5">Veedu Fine Dining</span>
            </div>
          )}

          {/* Vignette Shadow Gradients */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#251711] via-transparent to-black/40 pointer-events-none" />

          {/* Floating Badges on Photo */}
          <div className="absolute top-4 left-4 z-20 flex flex-wrap items-center gap-2 max-w-[80%]">
            {catLabel && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/65 backdrop-blur-md border border-[#C2A04A]/40 text-[#C2A04A] font-body text-[9px] tracking-[0.25em] uppercase font-semibold">
                {catLabel}
              </span>
            )}
            {item.journey && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#A8321D]/85 backdrop-blur-md text-[#F4ECD8] font-body text-[9px] tracking-[0.25em] uppercase font-bold border border-[#A8321D]">
                {item.journey === "coastal" ? "The Coastal Journey" : "The Pan Indian Journey"}
              </span>
            )}
          </div>

          {/* Chef's Pick Badge on Photo (Bottom Left) */}
          {item.featured && (
            <div className="absolute bottom-4 left-4 z-20 inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#C2A04A] text-[#1F120C] font-body text-[9px] tracking-[0.25em] uppercase font-bold shadow-lg">
              <Sparkles size={11} className="fill-current" /> Chef's Signature Pick
            </div>
          )}

          {/* Item Index Pill (Bottom Right) */}
          {totalItems > 1 && currentIndex > -1 && (
            <div className="absolute bottom-4 right-4 z-20 px-2.5 py-0.5 rounded-full bg-black/60 backdrop-blur-md text-[#D3C1AF] font-body text-[9px] tracking-widest uppercase">
              {currentIndex + 1} / {totalItems}
            </div>
          )}
        </div>

        {/* 2. Dish Information & Culinary Details */}
        <div className="p-6 sm:p-8 space-y-5">
          {/* Header Row: Title */}
          <div className="border-b border-[#C2A04A]/15 pb-4">
            <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-[#F6F2EA] tracking-tight leading-tight">
              {item.name}
            </h3>
          </div>

          {/* Description */}
          <p className="font-body text-sm sm:text-[15px] font-light text-[#D4C4B5] leading-relaxed">
            {item.desc || "An authentic creation marrying traditional Kerala heritage spices and contemporary fine-dining technique."}
          </p>

          {/* Dietary & Allergen Profile Grid */}
          <div className="rounded-xl bg-[#1A0E08]/80 border border-[#C2A04A]/20 p-4 space-y-3">
            <span className="block font-body text-[9.5px] tracking-[0.3em] uppercase text-[#C2A04A] font-semibold">
              Dietary & Flavour Profile
            </span>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {/* Veg / Non-Veg Indicator */}
              {item.veg ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#2F5D50] bg-[#2F5D50]/20 text-[#82b89d] text-xs font-body tracking-wide">
                  <VegLeafIcon /> Vegetarian Dish
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#A8321D]/50 bg-[#A8321D]/15 text-[#f09a89] text-xs font-body tracking-wide">
                  <NonVegBadgeIcon /> Non-Vegetarian
                </span>
              )}

              {/* Spice Level */}
              {item.spiceLevel > 0 ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#A8321D]/40 bg-[#A8321D]/10 text-[#F4ECD8] text-xs font-body">
                  <SpiceLevelIcons level={item.spiceLevel} />
                  <span>
                    {item.spiceLevel === 1 && "Mild Warmth"}
                    {item.spiceLevel === 2 && "Medium Spiced"}
                    {item.spiceLevel === 3 && "Fiery Bird's-Eye Heat"}
                  </span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-[#C2A04A]/20 bg-[#C2A04A]/5 text-[#D3C1AF] text-xs font-body">
                  Delicate · Non-Spicy
                </span>
              )}

              {/* Allergens */}
              {(item.cg || item.gf) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs font-body">
                  <span className="font-mono text-[10px] font-bold border border-current px-1 rounded-sm">G</span> Contains Gluten
                </span>
              )}

              {(item.cn || item.nut) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-200 text-xs font-body">
                  <span className="font-mono text-[10px] font-bold border border-current px-1 rounded-sm">N</span> Contains Nuts
                </span>
              )}

              {(item.cd || item.da) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-200 text-xs font-body">
                  <span className="font-mono text-[10px] font-bold border border-current px-1 rounded-sm">D</span> Contains Dairy
                </span>
              )}

              {!item.cg && !item.gf && !item.cn && !item.nut && !item.cd && !item.da && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#C2A04A]/20 bg-[#C2A04A]/5 text-[#C2A04A]/80 text-xs font-body">
                  No Major Allergens Listed
                </span>
              )}
            </div>
          </div>

          {/* Footer Call to Action */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <span className="text-[11px] font-body text-[#D3C1AF]/60 italic">
              Crafted fresh daily at #VEEDU by Chef Jomon
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => openReservationModal({ dishName: item.name })}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#C2A04A] bg-[#C2A04A] text-[#170E09] hover:bg-[#D4B35D] font-body text-[10px] tracking-[0.2em] uppercase font-bold transition-all shadow-md cursor-pointer"
              >
                Reserve Table
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full border border-white/20 text-[#F6F2EA]/80 hover:text-white hover:border-white/40 font-body text-[10px] tracking-[0.2em] uppercase transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ── Main Section ─────────────────────────────────────────── */
const MenuJourney = () => {
  const { openReservationModal } = useReservation();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [enlargedItem, setEnlargedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const status = useOpeningHours();
  const tabsRef = useRef(null);
  const mobileTabsRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [showMobileLeftArrow, setShowMobileLeftArrow] = useState(false);
  const [showMobileRightArrow, setShowMobileRightArrow] = useState(true);

  // Fetch dynamic menu from backend with graceful fallback
  useEffect(() => {
    let isMounted = true;
    const fetchMenu = async () => {
      try {
        const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
        const backendUrl = process.env.REACT_APP_BACKEND_URL || (isLocal ? "" : "https://api.chefjomonsveedu.co.uk");
        const primaryEndpoint = `${backendUrl}/api/public-menu`;
        let res;

        try {
          res = await fetch(primaryEndpoint);
          if (!res.ok) throw new Error(`Primary endpoint returned ${res.status}`);
        } catch (fetchErr) {
          // If in local browser and primary relative proxy missed, try direct local backend port 5011
          if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
            res = await fetch("http://localhost:5011/api/public-menu");
          } else {
            throw fetchErr;
          }
        }

        if (res && res.ok) {
          const result = await res.json();

          if (result.success && result.data && isMounted) {
            if (result.data.categories && result.data.categories.length > 0) {
              const mappedCategories = result.data.categories.map((c) => ({
                id: c.slug || `cat-${c.id}`,
                label: c.label || c.name,
                sublabel: c.sublabel || "CHEF'S SELECTION",
                short: c.short || c.label || c.name,
                icon: CATEGORY_ICON_MAP[c.slug] || Sparkles,
              }));

              // Append virtual full-menu category tab
              mappedCategories.push({
                id: "full-menu",
                label: "Full Menu",
                sublabel: "ALL SELECTIONS",
                short: "All",
                icon: Menu,
              });

              setCategories(mappedCategories);
              setActiveTab((prev) => (prev ? prev : mappedCategories[0].id));
            }

            if (result.data.items && Array.isArray(result.data.items)) {
              setMenuItems(result.data.items);
            }
          }
        }
      } catch (err) {
        console.warn("Notice: Backend public menu fetch fallback active:", err.message);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchMenu();
    return () => {
      isMounted = false;
    };
  }, []);

  const checkScroll = () => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      setShowLeftArrow(scrollLeft > 2);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 2);
    }
  };

  const checkMobileScroll = () => {
    if (mobileTabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = mobileTabsRef.current;
      setShowMobileLeftArrow(scrollLeft > 2);
      setShowMobileRightArrow(scrollLeft < scrollWidth - clientWidth - 2);
    }
  };

  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = 240;
      tabsRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const scrollMobileTabs = (direction) => {
    if (mobileTabsRef.current) {
      const scrollAmount = 160;
      mobileTabsRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const tabsContainer = tabsRef.current;
    const mobileContainer = mobileTabsRef.current;

    const onScroll = () => checkScroll();
    const onMobileScroll = () => checkMobileScroll();

    if (tabsContainer) tabsContainer.addEventListener("scroll", onScroll);
    if (mobileContainer) mobileContainer.addEventListener("scroll", onMobileScroll);

    checkScroll();
    checkMobileScroll();

    const timer = setTimeout(() => {
      checkScroll();
      checkMobileScroll();
    }, 100);

    window.addEventListener("resize", onScroll);
    window.addEventListener("resize", onMobileScroll);

    return () => {
      if (tabsContainer) tabsContainer.removeEventListener("scroll", onScroll);
      if (mobileContainer) mobileContainer.removeEventListener("scroll", onMobileScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("resize", onMobileScroll);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    checkScroll();
    checkMobileScroll();
  }, [activeTab]);

  const displayedItems = useMemo(() => {
    if (activeTab === "full-menu") return menuItems;
    return menuItems.filter((d) => d.category === activeTab);
  }, [activeTab, menuItems]);

  const activeCat = categories.find((c) => c.id === activeTab);

  return (
    <section
      id="table"
      data-chapter
      data-testid="menu-journey"
      className="relative bg-[#F6F2EA] text-[#382218] overflow-hidden py-0"
    >
      {/* ── Section Hero Header: 04 — THE MENU ────────────────────── */}
      <div className="relative pt-8 pb-5 md:pt-10 md:pb-6 px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-6 md:pr-12 lg:pr-16 w-full text-left border-b border-[#4A2F25]/12">
        <div className="flex items-center justify-center gap-4 mb-3">
          <span className="h-px w-10 bg-[#C4572D]/50" />
          <span className="font-body text-[9.5px] tracking-[0.45em] uppercase text-[#C4572D] font-semibold">
            03 — OUR MENU
          </span>
          <span className="h-px w-10 bg-[#C4572D]/50" />
        </div>
        <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-[#382218] tracking-tight leading-[1.05]">
          Our Menu
        </h1>
        <span className="mt-2 font-mal text-3xl md:text-4xl text-[#C2A04A] block leading-none">
          ഭക്ഷണം
        </span>
        <p className="mt-3 font-body text-xs md:text-sm font-light italic text-[#604B3E] tracking-wide max-w-md mx-auto">
          A celebration of timeless Kerala flavours, prepared for gathering and shared memories.
        </p>

        {/* ── Responsive Opening Hours & Live UK Status Bar ── */}
        <div data-testid="menu-opening-hours" className="mt-4 flex flex-wrap items-center justify-center gap-2.5 text-xs font-sans">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#4A2F25]/20 bg-[#EBE3D5] text-[#382218] shadow-sm">
            <Clock size={13} className="text-[#C4572D]" />
            <span className="font-semibold text-[10.5px] tracking-wide uppercase">Hours (UK):</span>
            <span className="font-medium text-[10.5px] text-[#604B3E]">
              Tue–Fri 16:30–23:00 <span className="text-[10px] text-[#8C7462]">(Last order 22:30)</span> • Sat–Sun 13:00–23:00 • Mon Closed
            </span>
          </div>

          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-sans font-bold tracking-wider uppercase shadow-sm ${
            status.isOpen
              ? status.isLastOrder
                ? "bg-amber-100 text-amber-900 border border-amber-300"
                : "bg-[#1b3b28] text-[#f5f0e8]"
              : "bg-[#382218]/10 text-[#5a4535] border border-[#382218]/20"
          }`}>
            <span className={`h-2 w-2 rounded-full ${
              status.isOpen
                ? status.isLastOrder
                  ? "bg-amber-500 animate-pulse"
                  : "bg-emerald-400 animate-pulse"
                : "bg-red-500"
            }`} />
            <span>{status.statusLabel}</span>
          </div>
        </div>

        {/* ── Legend ── */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 md:gap-6 font-body text-[9px] tracking-wider uppercase text-[#604B3E]">
          <div className="flex items-center gap-1.5">
            <VegLeafIcon /> <span className="pt-0.5">Vegetarian</span>
          </div>
          <div className="flex items-center gap-1.5">
            <SpiceLevelIcons level={1} /> <span className="pt-0.5">Mild</span>
          </div>
          <div className="flex items-center gap-1.5">
            <SpiceLevelIcons level={2} /> <span className="pt-0.5">Medium</span>
          </div>
          <div className="flex items-center gap-1.5">
            <SpiceLevelIcons level={3} /> <span className="pt-0.5">Spicy</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ContainsGlutenIcon /> <span className="pt-0.5">Contains Gluten (G)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ContainsNutIcon /> <span className="pt-0.5">Contains Nut (N)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ContainsDairyIcon /> <span className="pt-0.5">Contains lactose/Dairy (D)</span>
          </div>
        </div>
      </div>

      {/* ── Sticky Tab Header Bar (Premium Classic Menu Ribbon) ── */}
      <div className="sticky top-0 z-[110] w-full bg-[#F6F2EA]/96 backdrop-blur-md border-b border-[#C2A04A]/25 shadow-sm">
        {/* Desktop Navigation Ribbon */}
        <div className="hidden lg:flex items-center w-full px-6 lg:pl-[14%] xl:pl-[16%] lg:pr-16 xl:pr-20 py-2.5 gap-4">
          {/* Chapter badge — fixed left */}
          <div className="flex items-center gap-2.5 shrink-0 pr-5 border-r border-[#4A2F25]/15">
            <span className="font-body text-[10px] tracking-[0.45em] text-[#C4572D] font-bold">03</span>
            <span className="font-display text-lg font-normal text-[#382218] tracking-tight">Our Menu</span>
          </div>

          {/* Scrollable tab strip with fade edges */}
          <div className="relative flex-1 min-w-0">
            {/* Left fade */}
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-r from-[#F6F2EA]/95 to-transparent" />
            {/* Right fade */}
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-l from-[#F6F2EA]/95 to-transparent" />

            {/* Scroll navigation arrows */}
            {showLeftArrow && (
              <button
                onClick={() => scrollTabs("left")}
                className="absolute left-1 top-1/2 -translate-y-1/2 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-[#4A2F25]/20 bg-[#F6F2EA]/95 text-[#382218] shadow-md backdrop-blur-sm transition-all duration-200 hover:bg-[#C4572D] hover:text-[#F4ECD8] hover:border-[#C4572D] active:scale-95"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            {showRightArrow && (
              <button
                onClick={() => scrollTabs("right")}
                className="absolute right-1 top-1/2 -translate-y-1/2 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-[#4A2F25]/20 bg-[#F6F2EA]/95 text-[#382218] shadow-md backdrop-blur-sm transition-all duration-200 hover:bg-[#C4572D] hover:text-[#F4ECD8] hover:border-[#C4572D] active:scale-95"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            <div
              ref={tabsRef}
              className="flex items-center gap-1.5 overflow-x-auto py-1 px-8"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {categories.length > 0 ? (
                categories.map((cat) => {
                  const Icon = cat.icon || Sparkles;
                  const isActive = activeTab === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveTab(cat.id)}
                      className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full font-body text-[10px] tracking-[0.18em] uppercase font-semibold transition-all duration-200 ${isActive
                        ? "bg-[#C4572D] text-[#F4ECD8] shadow-md"
                        : "text-[#604B3E] hover:text-[#382218] hover:bg-[#4A2F25]/6"
                        }`}
                    >
                      <Icon className={`w-3 h-3 transition-colors ${isActive ? "text-[#F4ECD8]" : "text-[#C2A04A]"}`} />
                      <span>{cat.short}</span>
                    </button>
                  );
                })
              ) : (
                <div className="py-1 text-[10px] font-body text-[#8C7462] tracking-wider uppercase opacity-60">
                  Loading categories...
                </div>
              )}
            </div>
          </div>

          {/* Dish Count — fixed right */}
          <div className="shrink-0 pl-5 border-l border-[#4A2F25]/15">
            <span className="font-body text-[10px] tracking-[0.2em] text-[#604B3E] uppercase font-medium whitespace-nowrap">
              {displayedItems.length} Dishes
            </span>
          </div>
        </div>

        {/* Mobile / Tablet Navigation Ribbon */}
        <div className="lg:hidden bg-[#F6F2EA] px-4 pt-2.5 pb-2">
          <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-[#4A2F25]/12">
            <span className="font-body text-[9.5px] tracking-[0.4em] text-[#C4572D] uppercase font-bold">03 — Our Menu</span>
            <span className="font-body text-[9.5px] text-[#604B3E] font-medium">{displayedItems.length} dishes</span>
          </div>
          {/* Scrollable tab row with fade edges */}
          <div className="relative">
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 z-10 bg-gradient-to-r from-[#F6F2EA] to-transparent" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 z-10 bg-gradient-to-l from-[#F6F2EA] to-transparent" />

            {/* Scroll navigation arrows (Mobile) */}
            {showMobileLeftArrow && (
              <button
                onClick={() => scrollMobileTabs("left")}
                className="absolute left-0.5 top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-full border border-[#4A2F25]/20 bg-[#F6F2EA]/95 text-[#382218] shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-[#C4572D] hover:text-[#F4ECD8] hover:border-[#C4572D] active:scale-95"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
            )}
            {showMobileRightArrow && (
              <button
                onClick={() => scrollMobileTabs("right")}
                className="absolute right-0.5 top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-full border border-[#4A2F25]/20 bg-[#F6F2EA]/95 text-[#382218] shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-[#C4572D] hover:text-[#F4ECD8] hover:border-[#C4572D] active:scale-95"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}

            <div
              ref={mobileTabsRef}
              className="flex gap-1.5 overflow-x-auto py-1 px-7"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
            >
              {categories.length > 0 ? (
                categories.map((cat) => {
                  const Icon = cat.icon || Sparkles;
                  const isActive = activeTab === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveTab(cat.id)}
                      className={`shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full font-body text-[9px] tracking-[0.18em] uppercase font-semibold border transition-all ${isActive
                        ? "bg-[#C4572D] border-[#C4572D] text-[#F4ECD8] shadow-sm"
                        : "border-[#4A2F25]/20 text-[#604B3E] bg-white/40"
                        }`}
                    >
                      <Icon className={`w-3 h-3 ${isActive ? "text-[#F4ECD8]" : "text-[#C2A04A]"}`} />
                      {cat.short}
                    </button>
                  );
                })
              ) : (
                <div className="py-1 text-[9px] font-body text-[#8C7462] tracking-wider uppercase opacity-60">
                  Loading categories...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Full-Length Cream Canvas ────────────────────── */}
      <div className="relative py-6 md:py-8 px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-6 md:pr-12 lg:pr-16 w-full min-h-screen">
        {/* Header Title Section */}
        <div className="relative z-10 text-center mb-10 md:mb-14">
          {/* Micro Subtitle with Ornamental Lines */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="h-px w-8 md:w-12 bg-[#4A2F25]/25" />
            <span className="font-body text-[9.5px] tracking-[0.45em] uppercase text-[#A8321D] font-semibold">
              {activeCat?.sublabel || "CHEF'S SELECTION"}
            </span>
            <span className="h-px w-8 md:w-12 bg-[#4A2F25]/25" />
          </div>

          {/* Main Category Headline */}
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#382218] tracking-tight leading-none mt-1">
            {activeCat?.label || "House Specials"}
          </h2>

          {/* Green Leaf Filigree Ornament */}
          <div className="flex items-center justify-center gap-2 mt-4 mb-2">
            <span className="h-px w-16 bg-[#2F5D50]/30" />
            <svg viewBox="0 0 30 12" fill="none" className="w-8 h-3 text-[#2F5D50]">
              <path d="M15 1C10 5 4 6 0 6C4 6 10 7 15 11C20 7 26 6 30 6C26 6 20 5 15 1Z" fill="currentColor" opacity="0.75" />
            </svg>
            <span className="h-px w-16 bg-[#2F5D50]/30" />
          </div>

          {/* Tagline */}
          <p className="font-body text-xs md:text-sm font-light italic text-[#604B3E] tracking-wide max-w-md mx-auto">
            {activeTab === "tasting-menu"
              ? "Two distinct gastronomic journeys through the coastlines and heritage flavours of India."
              : "A celebration of bold flavours, crafted with passion."}
          </p>
        </div>

        {/* Animated Dynamic Menu Content */}
        <div className="relative z-10 min-h-[360px]">
          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center text-center">
              <div className="w-8 h-8 border-2 border-[#C2A04A] border-t-transparent rounded-full animate-spin mb-4" />
              <p className="font-display text-base tracking-[0.2em] uppercase text-[#8C7462]">
                Preparing Chef Jomon's Menu
              </p>
              <span className="text-[11px] font-body text-[#8C7462]/70 mt-1 italic tracking-wide">
                Loading fresh selections from the hearth...
              </span>
            </div>
          ) : menuItems.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-center max-w-md mx-auto">
              <span className="text-2xl text-[#C2A04A] mb-3">✦</span>
              <h3 className="font-display text-2xl font-light text-[#382218] tracking-tight">
                Seasonal Menu Curating
              </h3>
              <p className="mt-2 font-body text-xs md:text-sm font-light leading-relaxed text-[#604B3E]">
                Our culinary team is currently curating this season's selections. Please check back shortly or call our reservations desk to inquire today.
              </p>
              <div className="mt-5 flex items-center gap-3">
                <a
                  href={CONTACT.phoneHref}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4A2F25]/20 bg-[#EBE3D5] text-[#382218] font-body text-[10px] tracking-wider uppercase font-semibold hover:border-[#C4572D] hover:text-[#C4572D] transition-colors"
                >
                  📞 {CONTACT.phone}
                </a>
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab || "default"}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                {activeTab === "tasting-menu" ? (
                  <TastingMenuSectionedView items={menuItems} onSelect={setEnlargedItem} />
                ) : activeTab !== "full-menu" ? (
                  <MenuCanvasGrid items={displayedItems} onSelect={setEnlargedItem} />
                ) : (
                  <div>
                    {categories.filter((cat) => cat.id !== "full-menu").map((cat) => (
                      <FullMenuCategoryBlock key={cat.id} cat={cat} allItems={menuItems} onSelect={setEnlargedItem} />
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* ── Premium Full-Width Kerala Forest Green Separation Band ── */}
      <div className="relative w-full bg-[#1F4339] border-y border-[#C2A04A]/30 py-6 md:py-8 px-6 overflow-hidden">
        {/* Subtle background botanical leaf ghost */}
        <div className="pointer-events-none select-none absolute right-6 top-1/2 -translate-y-1/2 opacity-10">
          <svg viewBox="0 0 120 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-48 h-auto text-[#F4ECD8]">
            <path d="M60 210 Q58 140 55 80 Q52 40 50 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M55 170 Q30 155 22 140 Q38 145 55 155" stroke="currentColor" strokeWidth="1" />
            <path d="M57 148 Q82 133 90 118 Q74 123 57 133" stroke="currentColor" strokeWidth="1" />
            <path d="M56 120 Q31 105 23 90 Q39 95 56 105" stroke="currentColor" strokeWidth="1" />
            <path d="M57 96 Q82 81 91 66 Q75 71 57 81" stroke="currentColor" strokeWidth="1" />
          </svg>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center flex flex-col items-center">
          {/* Micro Subtitle */}
          <div className="flex items-center gap-4 mb-3 opacity-90">
            <span className="h-px w-10 bg-[#C2A04A]/60" />
            <span className="font-body text-[9.5px] tracking-[0.45em] uppercase text-[#C2A04A] font-semibold">
              Join Our Table
            </span>
            <span className="h-px w-10 bg-[#C2A04A]/60" />
          </div>

          {/* Main Transition Quote */}
          <h3 className="font-display text-xl md:text-2xl lg:text-3xl font-light text-[#F4ECD8] tracking-tight leading-tight max-w-2xl">
            "Experience the culture and food through our hospitality and dishes served"
          </h3>

          {/* CTA Button */}
          <div className="mt-8">
            <button
              type="button"
              onClick={() => openReservationModal()}
              className="inline-flex items-center justify-center px-8 py-3.5 border border-[#C2A04A]/70 text-[#F4ECD8] hover:bg-[#C2A04A] hover:text-[#1F4339] transition-all duration-300 rounded-full font-body text-[10px] tracking-[0.2em] uppercase font-semibold group cursor-pointer"
            >
              Reserve a Table
              <span className="ml-2 opacity-70 group-hover:opacity-100 transition-opacity">✦</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Dish Enlarge Lightbox Modal ── */}
      <AnimatePresence>
        {enlargedItem && (
          <DishEnlargeModal
            item={enlargedItem}
            items={
              activeTab === "full-menu"
                ? menuItems
                : activeTab === "tasting-menu"
                ? menuItems.filter((d) => d.category === "tasting-menu" || d.category === "tasting")
                : displayedItems
            }
            categories={categories}
            onClose={() => setEnlargedItem(null)}
            onSelect={setEnlargedItem}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default MenuJourney;
